/**
 * ThumbRailNav — primary navigation for TABLET (below `lg`).
 *
 * A bottom thumb-rail reachable one-handed, showing the primary sections
 * (NAV_PRIMARY). On desktop (`lg`+) the DomainRail replaces this with the
 * fly-out domain menu. Each target honours the 44px touch floor + focus ring.
 */
import { NavLink } from "react-router-dom";
import { useSession } from "@/stores/session";
import { NAV_PRIMARY } from "./navItems";
import { scopedHref } from "@/lib/navModel";
import { cn } from "@/lib/cn";

/** Build the concrete href for a primary section under the current scope. Same rule as the
 *  desktop rail (lib/navModel.scopedHref): Overview always links at customer level, and Health
 *  is the explicit /dashboard path now that the customer index lands on the Overview. */
function useScopedHref() {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return (item: { section: string; customerLevel?: boolean }): string =>
    scopedHref(item, orgId, siteId);
}

/** Bottom thumb-rail — visible below `lg`, hidden on desktop. */
export function ThumbRailNav() {
  const href = useScopedHref();
  return (
    <nav
      aria-label="Primary"
      className={cn(
        "fixed inset-x-0 bottom-0 z-rail h-thumb-rail border-t border-line bg-surface lg:hidden",
        "pb-[env(safe-area-inset-bottom)]",
      )}
    >
      <ul className="mx-auto flex h-thumb-rail max-w-2xl items-stretch justify-around">
        {NAV_PRIMARY.map((item) => (
          <li key={item.section} className="flex-1">
            <NavLink
              to={href(item)}
              className={({ isActive }) =>
                cn(
                  "flex h-full min-h-touch flex-col items-center justify-center gap-0.5 text-xs",
                  isActive ? "text-brand" : "text-ink-muted",
                )
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
